/**
 * Clip Thumbnails Hook
 *
 * Client-side thumbnail generation from video elements.
 * Generates base64-encoded thumbnail frames progressively without blocking UI.
 *
 * V2: Uses 1:1 square thumbnails (96x96) instead of 16:9 (170x96).
 */

import { useEffect, useRef, useState } from 'react'
import type { TimelineClip } from './types'
import { authenticatedFetch } from '../../../../api/client'

/**
 * Number of thumbnails to generate per clip (max)
 * Will be clamped to duration and filmstrip width needs
 */
const DEFAULT_THUMBNAIL_COUNT = 10

/**
 * Thumbnail dimensions — 1:1 square aspect ratio
 */
const THUMBNAIL_WIDTH = 96
const THUMBNAIL_HEIGHT = 96

/**
 * Hook to generate thumbnails for a single clip
 *
 * @param clip - The timeline clip to generate thumbnails for
 * @param sourcePath - Path to the video file
 * @returns Array of base64-encoded thumbnail URLs
 */
export function useClipThumbnails(
  clip: TimelineClip,
  sourcePath: string
): string[] {
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const generationInProgressRef = useRef<boolean>(false)

  useEffect(() => {
    // Skip if already generating or no source path
    if (generationInProgressRef.current || !sourcePath) {
      return
    }

    // Skip if thumbnails already exist for this clip
    if (clip.thumbnails && clip.thumbnails.length > 0) {
      setThumbnails(clip.thumbnails)
      return
    }

    // Create canvas for frame capture
    const canvas = document.createElement('canvas')
    canvas.width = THUMBNAIL_WIDTH
    canvas.height = THUMBNAIL_HEIGHT
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      console.error('Failed to get canvas context for thumbnails')
      return
    }

    canvasRef.current = canvas
    generationInProgressRef.current = true

    const generateThumbnails = async () => {
      let blobUrl: string | null = null
      try {
        // Fetch video as blob to bypass CORS restrictions
        const response = await authenticatedFetch(sourcePath)
        const blob = await response.blob()
        blobUrl = URL.createObjectURL(blob)

        // Create hidden video element with blob URL (no crossOrigin needed)
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true
        video.playsInline = true
        videoRef.current = video

        // Wait for video metadata to load
        await new Promise<void>((resolve, reject) => {
          video.onloadedmetadata = () => resolve()
          video.onerror = () => reject(new Error('Failed to load video metadata'))
          video.src = blobUrl!
        })

        // Use actual video duration instead of clip.originalDuration (may be stale)
        const duration = video.duration
        const thumbnailCount = Math.min(DEFAULT_THUMBNAIL_COUNT, Math.ceil(duration))
        const interval = duration / thumbnailCount
        const newThumbnails: string[] = []

        // Generate thumbnails at intervals
        for (let i = 0; i < thumbnailCount; i++) {
          const time = clip.trimStart + (i * interval)

          // Ensure we don't exceed trimEnd
          if (time >= (duration - clip.trimEnd)) {
            break
          }

          // Seek to timestamp
          await new Promise<void>((resolve, reject) => {
            video.onseeked = () => resolve()
            video.onerror = () => reject(new Error('Failed to seek video'))
            video.currentTime = time
          })

          // Small delay to ensure frame is ready
          await new Promise(resolve => setTimeout(resolve, 50))

          // Draw current frame to canvas
          ctx.drawImage(video, 0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT)

          // Convert to base64
          const thumbnail = canvas.toDataURL('image/jpeg', 0.6)
          newThumbnails.push(thumbnail)

          // Update state progressively (optional, for immediate feedback)
          setThumbnails([...newThumbnails])
        }

        // Final update
        setThumbnails(newThumbnails)
      } catch (error) {
        console.error('Error generating thumbnails:', error)
        setThumbnails([])
      } finally {
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl)
        }
        generationInProgressRef.current = false
      }
    }

    // Start generation (async, non-blocking)
    generateThumbnails()

    // Cleanup
    return () => {
      if (videoRef.current) {
        videoRef.current.src = ''
        videoRef.current = null
      }
      canvasRef.current = null
      generationInProgressRef.current = false
    }
  }, [clip.id, clip.originalDuration, clip.trimStart, clip.trimEnd, sourcePath, clip.thumbnails])

  return thumbnails
}

/**
 * Hook to batch-generate thumbnails for multiple clips
 *
 * Processes clips sequentially to avoid overwhelming the browser
 *
 * @param clips - Array of timeline clips
 * @returns Map of clip IDs to thumbnail arrays
 */
export function useBatchClipThumbnails(
  clips: TimelineClip[]
): Map<string, string[]> {
  const [thumbnailMap, setThumbnailMap] = useState<Map<string, string[]>>(new Map())
  const processingRef = useRef<boolean>(false)

  useEffect(() => {
    if (processingRef.current || clips.length === 0) {
      return
    }

    processingRef.current = true

    const generateBatchThumbnails = async () => {
      const newMap = new Map<string, string[]>()

      for (const clip of clips) {
        // Skip if already has thumbnails
        if (clip.thumbnails && clip.thumbnails.length > 0) {
          newMap.set(clip.id, clip.thumbnails)
          continue
        }

        try {
          const thumbnails = await generateClipThumbnails(clip)
          newMap.set(clip.id, thumbnails)

          // Update state progressively
          setThumbnailMap(new Map(newMap))

          // Small delay between clips to prevent browser freeze
          await new Promise(resolve => setTimeout(resolve, 100))
        } catch (error) {
          console.error(`Error generating thumbnails for clip ${clip.id}:`, error)
          newMap.set(clip.id, [])
        }
      }

      setThumbnailMap(newMap)
      processingRef.current = false
    }

    generateBatchThumbnails()

    return () => {
      processingRef.current = false
    }
  }, [clips])

  return thumbnailMap
}

/**
 * Helper function to generate thumbnails for a single clip
 * Returns a promise that resolves with thumbnail array
 */
async function generateClipThumbnails(clip: TimelineClip): Promise<string[]> {
  let blobUrl: string | null = null

  try {
    // Fetch video as blob to bypass CORS restrictions
    const response = await authenticatedFetch(clip.sourcePath)
    const blob = await response.blob()
    blobUrl = URL.createObjectURL(blob)

    return await new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true
      // No crossOrigin needed with blob URL

      const canvas = document.createElement('canvas')
      canvas.width = THUMBNAIL_WIDTH
      canvas.height = THUMBNAIL_HEIGHT
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      video.onloadedmetadata = async () => {
        try {
          // Use actual video duration instead of clip.originalDuration
          const duration = video.duration
          const thumbnailCount = Math.min(DEFAULT_THUMBNAIL_COUNT, Math.ceil(duration))
          const interval = duration / thumbnailCount
          const thumbnails: string[] = []

          for (let i = 0; i < thumbnailCount; i++) {
            const time = clip.trimStart + (i * interval)

            if (time >= (duration - clip.trimEnd)) {
              break
            }

            await new Promise<void>((seekResolve, seekReject) => {
              video.onseeked = () => seekResolve()
              video.onerror = () => seekReject(new Error('Seek failed'))
              video.currentTime = time
            })

            await new Promise(resolve => setTimeout(resolve, 50))

            ctx.drawImage(video, 0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT)
            const thumbnail = canvas.toDataURL('image/jpeg', 0.6)
            thumbnails.push(thumbnail)
          }

          video.src = ''
          resolve(thumbnails)
        } catch (error) {
          video.src = ''
          reject(error)
        }
      }

      video.onerror = () => {
        video.src = ''
        reject(new Error('Failed to load video'))
      }

      video.src = blobUrl!
    })
  } finally {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl)
    }
  }
}
